import { CSSProperties } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { WorkspaceSummary } from '../../../lib/workspaces';
import { Button, Card } from '../ui';
import { WorkspaceBadge } from '../WorkspaceBadge';
import styles from './BriefSlot.module.css';

// Mobile-card rendering of the "a new workspace created for you" brief state — the flat desktop content
// (`NewWorkspaceBrief`) as a floating `Card`, matching the native `BriefNewWorkspaceCard`
// (Figma 2057:10435): the sentence as a heavy-sans headline, the workspace badge + name beneath, and
// two hug shortcuts — Agent Work (solid) + Growth (outlined), each with a trailing ↗. No eyebrow and no
// description line (both dropped in the redesign).

// Headline — the heavy display sans (Archivo SemiBold 32/32), phone-sized like the sibling cards.
const HEADLINE_SANS: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 32,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '32px',
  letterSpacing: '-0.96px',
  color: 'rgb(var(--ink-rgb))',
};

// Workspace name — Archivo SemiBold 20/24 (Figma Display 04), beside the badge.
const NAME_TEXT: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontSize: 20,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '24px',
  letterSpacing: '-0.4px',
  color: 'rgb(var(--ink-rgb))',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function MobileNewWorkspaceBrief({ workspace }: { workspace?: WorkspaceSummary }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  if (!workspace) return null;

  const path = `/workspace/${encodeURIComponent(workspace.slug)}`;
  const click = (button_name: string) => () => logPageClickBi({ page_name: 'home', button_name });

  return (
    <Card component="section" style={{ textAlign: 'start' }} aria-labelledby="brief-new-workspace-title">
      <div id="brief-new-workspace-title" style={HEADLINE_SANS}>
        {t('brief.newWorkspace.title')}
      </div>
      <div className={styles.mobileName}>
        <WorkspaceBadge ws={workspace} size={20} />
        <span style={NAME_TEXT}>{workspace.name}</span>
      </div>
      <div className={styles.actions}>
        <Button
          href={`${path}/agents`}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_agent_work')}
        >
          {t('brief.newWorkspace.agentWork')}
        </Button>
        <Button
          variant="outlined"
          href={path}
          endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
          onClick={click('brief_new_workspace_growth')}
        >
          {t('brief.newWorkspace.growth')}
        </Button>
      </div>
    </Card>
  );
}
